import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMyActivity, type MyActivity } from '@/api/auth';
import {
  getCharacters,
  getTaskOutputs,
  getAssetsByTasks,
  type DbCharacter,
  type DbTaskOutput,
  type DbAsset,
} from '@/api/astraea';

const ACTION_LABEL: Record<string, string> = {
  strategy: '生成本周策略',
  content: '生成内容运营包',
  avatar: '生成真人形象',
  video: '生成影片',
  voice: '生成中文配音',
};

function labelOf(reason: string | null): string {
  if (!reason) return '操作';
  if (ACTION_LABEL[reason]) return ACTION_LABEL[reason];
  if (reason.startsWith('admin_adjust')) return '管理员调整点数';
  if (reason.startsWith('admin_set')) return '管理员设定点数';
  return reason;
}

export default function MePage() {
  const nav = useNavigate();
  const [acts, setActs] = useState<MyActivity[]>([]);
  const [chars, setChars] = useState<Record<number, DbCharacter>>({});
  const [outputs, setOutputs] = useState<Record<number, DbTaskOutput>>({});
  const [assets, setAssets] = useState<Record<number, DbAsset[]>>({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await getMyActivity(200);
      setActs(a);
      const taskIds = [...new Set(a.map((x) => x.task_id).filter((x): x is number => !!x))];
      const cs = await getCharacters();
      const cmap: Record<number, DbCharacter> = {};
      for (const c of cs) cmap[c.id] = c;
      setChars(cmap);
      if (taskIds.length) {
        const outs = await getTaskOutputs(taskIds);
        const omap: Record<number, DbTaskOutput> = {};
        for (const o of outs) if (!omap[o.task_id]) omap[o.task_id] = o;
        setOutputs(omap);
        const ast = await getAssetsByTasks(taskIds);
        const amap: Record<number, DbAsset[]> = {};
        for (const x of ast) { if (x.task_id) { (amap[x.task_id] ||= []).push(x); } }
        setAssets(amap);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/board" className="hover:text-foreground-600">工作台</Link>
        <i className="ri-arrow-right-s-line"></i><span>我的纪录</span>
      </div>
      <h1 className="text-2xl font-bold text-foreground-950 mb-1">我的纪录</h1>
      <p className="text-sm text-foreground-600 mb-6">你的操作步骤与生成历史。点开可查看当时生成的内容、影片与对应角色。</p>

      {loading ? (
        <div className="py-16 text-center text-sm text-secondary-400">读取中…</div>
      ) : acts.length === 0 ? (
        <div className="py-16 text-center text-sm text-secondary-400">还没有纪录,去工作台生成点东西吧。</div>
      ) : (
        <div className="space-y-2">
          {acts.map((a) => {
            const ch = a.character_id ? chars[a.character_id] : null;
            const out = a.task_id ? outputs[a.task_id] : null;
            const vids = a.task_id ? (assets[a.task_id] || []) : [];
            const canOpen = !!(out || vids.length);
            const canGoChar = !canOpen && !!a.character_id; // 没有可展开内容但有角色(如策略)→ 点了跳任务板
            const clickable = canOpen || canGoChar;
            const open = openId === a.id;
            return (
              <div key={a.id} className="rounded-xl border border-secondary-200 bg-background-50">
                <button
                  onClick={() => { if (canOpen) setOpenId(open ? null : a.id); else if (canGoChar) nav(`/board/${a.character_id}`); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left ${clickable ? 'cursor-pointer hover:bg-background-100' : 'cursor-default'}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${a.reason === 'video' ? 'ri-movie-2-line' : a.reason === 'voice' ? 'ri-mic-line' : a.reason === 'avatar' ? 'ri-camera-line' : 'ri-sparkling-line'} text-primary-600`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground-900">{labelOf(a.reason)}{ch ? <span className="text-secondary-400 font-normal"> · {ch.name}</span> : null}</div>
                    <div className="text-[11px] text-secondary-400">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`text-sm font-semibold ${a.amount < 0 ? 'text-red-500' : 'text-green-600'}`}>{a.amount > 0 ? '+' : ''}{a.amount}</span>
                  {canOpen ? <i className={`ri-arrow-down-s-line text-secondary-400 transition-transform ${open ? 'rotate-180' : ''}`}></i> : canGoChar ? <i className="ri-arrow-right-s-line text-secondary-400"></i> : null}
                </button>

                {open && canOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-secondary-100">
                    {ch && (
                      <div className="flex items-center gap-2 mb-2 mt-2">
                        {ch.avatar_url && <img src={ch.avatar_url} alt={ch.name} className="w-8 h-8 rounded-full object-cover" />}
                        <span className="text-xs text-foreground-700">角色:{ch.name}</span>
                        <Link to={`/board/${ch.id}`} className="text-[11px] text-primary-600 hover:underline">前往角色任务板 →</Link>
                      </div>
                    )}
                    {out && (
                      <div className="rounded-lg bg-background-100 border border-secondary-200 p-3 mb-2">
                        <div className="text-xs font-semibold text-foreground-800 mb-1">{out.title}</div>
                        <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-wrap line-clamp-6">{out.body}</p>
                      </div>
                    )}
                    {vids.filter((v) => v.asset_type === 'video' && v.url).map((v) => (
                      <video key={v.id} src={v.url as string} controls className="w-full rounded-lg bg-black max-h-72 mb-2" />
                    ))}
                    {vids.filter((v) => v.asset_type === 'audio' && v.url).map((v) => (
                      <audio key={v.id} src={v.url as string} controls className="w-full mb-2" />
                    ))}
                    {!out && vids.length === 0 && <div className="text-[11px] text-secondary-400 mt-2">此笔无可预览的素材</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
